import type { ProductBacklogSaveDraftInput } from '@cortex/domain-ac-mens-hub-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { rpcClient } from '../lib/orpc.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * 自分の並べ替えの下書きを取る（動かす権限がある人だけ。無ければ呼ばない）。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 動かす人の案件の並べ替えの下書きを取得する React Query フック
 * @graph-connects rpcClient [calls] oRPC productBacklogWrite.draft 呼び出し
 */
export function useProductBacklogDraft(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.productBacklog.draft,
    queryFn: () => rpcClient.productBacklogWrite.draft(),
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * 動かした記録を取る（記録の小窓を開いたときと、反映・元に戻すの後）。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件を動かした記録（新しい順）と元に戻せる直前の反映を取得する React Query フック
 * @graph-connects rpcClient [calls] oRPC productBacklog.moves 呼び出し
 */
export function useProductBacklogMoves(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.productBacklog.moves,
    queryFn: () => rpcClient.productBacklog.moves(),
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * 下書きの保存と捨てるを、送った順に1つずつ処理する（続けて札を動かしても、後の保存が先の保存に負けない）。
 * @graph-connects none
 */
const DRAFT_MUTATION_SCOPE = { id: 'product-backlog-draft' };

/**
 * 下書きを保存する（保存した下書きをそのまま下書きの問い合わせに入れる）。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件の並べ替えの下書きを保存し、保存した下書きを画面の下書きにするミューテーションフック
 * @graph-connects rpcClient [calls] oRPC productBacklogWrite.saveDraft 呼び出し
 */
export function useSaveProductBacklogDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProductBacklogSaveDraftInput) =>
      rpcClient.productBacklogWrite.saveDraft(input),
    scope: DRAFT_MUTATION_SCOPE,
    onSuccess: (response) => {
      queryClient.setQueryData(queryKeys.productBacklog.draft, response);
    },
  });
}

/**
 * 下書きを捨てる。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件の並べ替えの下書きを捨て、画面の下書きを空にするミューテーションフック
 * @graph-connects rpcClient [calls] oRPC productBacklogWrite.discardDraft 呼び出し
 */
export function useDiscardProductBacklogDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => rpcClient.productBacklogWrite.discardDraft(),
    scope: DRAFT_MUTATION_SCOPE,
    onSuccess: (response) => {
      queryClient.setQueryData(queryKeys.productBacklog.draft, response);
    },
  });
}

/**
 * 反映か元に戻すの後に、一覧・下書き・記録を読み直す。
 * @graph-connects none
 */
function invalidateAfterWrite(queryClient: ReturnType<typeof useQueryClient>): void {
  for (const queryKey of [
    queryKeys.productBacklog.list,
    queryKeys.productBacklog.draft,
    queryKeys.productBacklog.moves,
  ]) {
    void queryClient.invalidateQueries({ queryKey });
  }
}

/**
 * 下書きを全社スプシへ反映する（結果は outcome で返る。反映できなかった理由もエラーにはならない）。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件の並べ替えの下書きを全社スプシへ反映し、一覧・下書き・記録を読み直すミューテーションフック
 * @graph-connects rpcClient [calls] oRPC productBacklogWrite.apply 呼び出し
 * @graph-connects invalidateAfterWrite [calls] 読み直し
 */
export function useApplyProductBacklog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draftUpdatedAt: string) => rpcClient.productBacklogWrite.apply({ draftUpdatedAt }),
    onSettled: () => {
      invalidateAfterWrite(queryClient);
    },
  });
}

/**
 * 直前の反映を元に戻す。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 直前に反映した並べ替えを元に戻し、一覧・下書き・記録を読み直すミューテーションフック
 * @graph-connects rpcClient [calls] oRPC productBacklogWrite.undo 呼び出し
 * @graph-connects invalidateAfterWrite [calls] 読み直し
 */
export function useUndoProductBacklog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) => rpcClient.productBacklogWrite.undo({ batchId }),
    onSettled: () => {
      invalidateAfterWrite(queryClient);
    },
  });
}
